import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown, 
  Target, 
  Calendar, 
  Dumbbell, 
  CheckCircle, 
  Clock,
  BarChart3,
  Activity,
  Award,
  Zap,
  Users,
  Timer,
  Weight,
  Hash
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface ExerciseLog {
  id: string;
  performed_reps: number;
  performed_weight: number;
  notes: string;
  logged_at: string;
  user_exercises: {
    exercise_id: string;
    exercises: {
      name: string;
      description: string;
    };
  };
}

interface UserExercise {
  id: string;
  exercise_id: string;
  scheduled_date: string;
  sets: number;
  reps: number;
  weight: number | null;
  exercises: {
    name: string;
    description: string;
  };
  exercise_logs: ExerciseLog[];
}

interface UserProgressDashboardProps {
  userId: string;
}

export const UserProgressDashboard: React.FC<UserProgressDashboardProps> = ({ userId }) => {
  const [userExercises, setUserExercises] = useState<UserExercise[]>([]);
  const [exerciseLogs, setExerciseLogs] = useState<ExerciseLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'all'>('week');

  useEffect(() => {
    loadUserData();
  }, [userId]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!userId) {
        setError('No user ID provided');
        return;
      }
      
      // Load user exercises (exact same query as Dashboard.tsx)
      const { data: exercisesData, error: exercisesError } = await supabase
        .from('user_exercises')
        .select(`
          id,
          exercise_id,
          scheduled_date,
          sets,
          reps,
          weight,
          exercises!inner (
            name,
            description
          ),
          exercise_logs (
            id,
            performed_reps,
            performed_weight,
            notes,
            logged_at
          )
        `)
        .eq('user_id', userId)
        .order('scheduled_date', { ascending: true });

      if (exercisesError) {
        console.error('❌ Error loading user exercises:', exercisesError);
        console.error('❌ User ID:', userId);
        setError(`Failed to load user exercises: ${exercisesError.message}`);
        return;
      }

      // Transform data to match our interface (exact same as Dashboard.tsx)
      const typedExercisesData = (exercisesData || []).map(item => ({
        ...item,
        exercises: Array.isArray(item.exercises) ? item.exercises[0] : item.exercises
      }));
      
      setUserExercises(typedExercisesData);

      // Extract and flatten exercise logs from the main query
      const allLogs: ExerciseLog[] = [];
      typedExercisesData.forEach(exercise => {
        exercise.exercise_logs?.forEach(log => {
          allLogs.push({
            id: log.id,
            performed_reps: log.performed_reps,
            performed_weight: log.performed_weight,
            notes: log.notes,
            logged_at: log.logged_at,
            user_exercises: {
              exercise_id: exercise.exercise_id,
              exercises: exercise.exercises
            }
          });
        });
      });

      setExerciseLogs(allLogs);
      
      console.log('📊 Loaded data:', {
        userId,
        exercises: typedExercisesData.length,
        logs: allLogs.length,
        exerciseIds: typedExercisesData.map(ex => ex.id)
      });
    } catch (error) {
      console.error('💥 Unexpected error loading user data:', error);
      setError('An unexpected error occurred while loading data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate overall statistics
  const getOverallStats = () => {
    const totalExercises = userExercises.length;
    const uniqueExercises = new Set(userExercises.map(ex => ex.exercise_id)).size;
    const activeDays = new Set(userExercises.map(ex => ex.scheduled_date)).size;
    const totalWorkouts = exerciseLogs.length;
    
    // Calculate completed exercises by checking which user_exercises have logs
    const completedExerciseIds = new Set(exerciseLogs.map(log => log.user_exercises.exercise_id));
    const completedExercises = userExercises.filter(ex => completedExerciseIds.has(ex.exercise_id)).length;
    
    return {
      totalExercises,
      uniqueExercises,
      activeDays,
      totalWorkouts,
      completedExercises,
      completionRate: totalExercises > 0 ? (completedExercises / totalExercises) * 100 : 0
    };
  };

  // Calculate progress trends
  const getProgressTrends = () => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const recentLogs = exerciseLogs.filter(log => new Date(log.logged_at) >= weekAgo);
    const monthlyLogs = exerciseLogs.filter(log => new Date(log.logged_at) >= monthAgo);

    return {
      weeklyWorkouts: recentLogs.length,
      monthlyWorkouts: monthlyLogs.length,
      weeklyTrend: recentLogs.length > 0 ? 'up' : 'stable',
      monthlyTrend: monthlyLogs.length > 0 ? 'up' : 'stable'
    };
  };

  // Get exercise performance data
  const getExercisePerformance = () => {
    const exerciseStats: {[key: string]: {
      name: string;
      category: string;
      totalWorkouts: number;
      bestWeight: number;
      bestReps: number;
      avgWeight: number;
      avgReps: number;
      lastWorkout: string;
      improvement: number;
    }} = {};

    exerciseLogs.forEach(log => {
      const exerciseId = log.user_exercises.exercise_id;
      const exerciseName = log.user_exercises.exercises.name;
      const description = log.user_exercises.exercises.description;

      if (!exerciseStats[exerciseId]) {
        exerciseStats[exerciseId] = {
          name: exerciseName,
          category: description, // Use description as category
          totalWorkouts: 0,
          bestWeight: 0,
          bestReps: 0,
          avgWeight: 0,
          avgReps: 0,
          lastWorkout: '',
          improvement: 0
        };
      }

      const stats = exerciseStats[exerciseId];
      stats.totalWorkouts++;
      stats.bestWeight = Math.max(stats.bestWeight, log.performed_weight);
      stats.bestReps = Math.max(stats.bestReps, log.performed_reps);
      stats.lastWorkout = log.logged_at;
    });

    // Calculate averages and improvements
    Object.values(exerciseStats).forEach(stats => {
      const exerciseId = Object.keys(exerciseStats).find(id => exerciseStats[id] === stats);
      const exerciseLogsForStats = exerciseLogs.filter(log => 
        log.user_exercises.exercise_id === exerciseId
      );
      
      if (exerciseLogsForStats.length > 0) {
        stats.avgWeight = exerciseLogsForStats.reduce((sum, log) => sum + log.performed_weight, 0) / exerciseLogsForStats.length;
        stats.avgReps = exerciseLogsForStats.reduce((sum, log) => sum + log.performed_reps, 0) / exerciseLogsForStats.length;
        
        // Calculate improvement (latest vs first)
        const sortedLogs = exerciseLogsForStats.sort((a, b) => new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime());
        if (sortedLogs.length > 1) {
          const first = sortedLogs[0];
          const latest = sortedLogs[sortedLogs.length - 1];
          stats.improvement = ((latest.performed_weight - first.performed_weight) / first.performed_weight) * 100;
        }
      }
    });

    return Object.values(exerciseStats).sort((a, b) => b.totalWorkouts - a.totalWorkouts);
  };

  // Get weekly progress data
  const getWeeklyProgress = () => {
    const now = new Date();
    const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    const weeklyLogs = exerciseLogs.filter(log => {
      const logDate = new Date(log.logged_at);
      return logDate >= weekStart && logDate <= now;
    });
    
    const dailyStats: {[key: string]: number} = {};
    
    // Initialize all days with 0
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart.getTime() + i * 24 * 60 * 60 * 1000);
      const dateStr = date.toISOString().split('T')[0];
      dailyStats[dateStr] = 0;
    }
    
    // Count workouts per day
    weeklyLogs.forEach(log => {
      const dateStr = log.logged_at.split('T')[0];
      if (dailyStats.hasOwnProperty(dateStr)) {
        dailyStats[dateStr]++;
      }
    });
    
    return Object.entries(dailyStats).map(([date, count]) => ({
      date,
      count,
      dayName: new Date(date).toLocaleDateString('ro-RO', { weekday: 'short' })
    }));
  };

  // Memoize calculations for better performance
  const overallStats = useMemo(() => getOverallStats(), [userExercises, exerciseLogs]);
  const progressTrends = useMemo(() => getProgressTrends(), [exerciseLogs]);
  const exercisePerformance = useMemo(() => getExercisePerformance(), [exerciseLogs]);
  const weeklyProgress = useMemo(() => getWeeklyProgress(), [exerciseLogs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading progress data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-red-500 mb-4">❌</div>
          <p className="text-red-500 mb-2">Error loading data</p>
          <p className="text-muted-foreground text-sm">{error}</p>
          <Button 
            variant="outline" 
            onClick={loadUserData}
            className="mt-4"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Show empty state if no data
  if (userExercises.length === 0 && exerciseLogs.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-gray-400 mb-4">📊</div>
          <p className="text-muted-foreground mb-2">No data available</p>
          <p className="text-muted-foreground text-sm">
            This user hasn't been assigned any exercises yet or hasn't logged any workouts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <h2 className="text-lg sm:text-xl font-bold text-foreground">Dashboard de Progres</h2>
            <Badge className="bg-purple-600 text-white text-xs self-start sm:self-center">
              <Users className="h-3 w-3 mr-1" />
              Admin Only
            </Badge>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Analiza completă a progresului utilizatorilor</p>
        </div>
        <div className="flex gap-1 overflow-x-auto">
          <Button 
            variant={selectedPeriod === 'week' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setSelectedPeriod('week')}
            className="text-xs px-2 py-1 whitespace-nowrap"
          >
            Săptămâna
          </Button>
          <Button 
            variant={selectedPeriod === 'month' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setSelectedPeriod('month')}
            className="text-xs px-2 py-1 whitespace-nowrap"
          >
            Luna
          </Button>
          <Button 
            variant={selectedPeriod === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setSelectedPeriod('all')}
            className="text-xs px-2 py-1 whitespace-nowrap"
          >
            Tot timpul
          </Button>
        </div>
      </div>

      {/* Overall Statistics - Mobile Responsive */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <Card className="bg-gradient-to-br from-background via-background to-muted/20 border-border">
          <CardContent className="p-2 sm:p-3">
            <div className="flex items-center space-x-1 sm:space-x-2">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <Dumbbell className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
              <div>
                <p className="text-sm sm:text-lg font-bold text-foreground">{overallStats.totalExercises}</p>
                <p className="text-xs text-muted-foreground">Exerciții Totale</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-background via-background to-muted/20 border-border">
          <CardContent className="p-2 sm:p-3">
            <div className="flex items-center space-x-1 sm:space-x-2">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
              <div>
                <p className="text-sm sm:text-lg font-bold text-foreground">{overallStats.completedExercises}</p>
                <p className="text-xs text-muted-foreground">Finalizate</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-background via-background to-muted/20 border-border">
          <CardContent className="p-2 sm:p-3">
            <div className="flex items-center space-x-1 sm:space-x-2">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
              <div>
                <p className="text-sm sm:text-lg font-bold text-foreground">{overallStats.totalWorkouts}</p>
                <p className="text-xs text-muted-foreground">Antrenamente</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-background via-background to-muted/20 border-border">
          <CardContent className="p-2 sm:p-3">
            <div className="flex items-center space-x-1 sm:space-x-2">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <Target className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
              <div>
                <p className="text-sm sm:text-lg font-bold text-foreground">{overallStats.completionRate.toFixed(0)}%</p>
                <p className="text-xs text-muted-foreground">Rata Finalizare</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress Charts - Mobile Responsive */}
      <Tabs defaultValue="overview" className="space-y-3">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-8 sm:h-9">
          <TabsTrigger value="overview" className="text-xs py-1 px-2">Prezentare Generală</TabsTrigger>
          <TabsTrigger value="exercises" className="text-xs py-1 px-2">Exerciții</TabsTrigger>
          <TabsTrigger value="weekly" className="text-xs py-1 px-2">Progres Săptămânal</TabsTrigger>
          <TabsTrigger value="performance" className="text-xs py-1 px-2">Performanță</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Weekly Progress Chart */}
            <Card className="bg-gradient-to-br from-background via-background to-muted/20 border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-foreground text-sm">
                  <BarChart3 className="h-4 w-4 text-blue-500" />
                  Progres Săptămânal
                </CardTitle>
              </CardHeader>
              <CardContent>
                        <div className="space-y-1 sm:space-y-2">
                          {weeklyProgress.map((day, index) => (
                            <div key={day.date} className="flex items-center justify-between">
                              <div className="flex items-center gap-1 sm:gap-2">
                                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                  {day.dayName}
                                </div>
                                <span className="text-xs text-foreground">{day.count} antrenamente</span>
                              </div>
                              <div className="w-16 sm:w-24 bg-gray-700 rounded-full h-1 sm:h-1.5">
                                <div 
                                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-1 sm:h-1.5 rounded-full transition-all duration-500"
                                  style={{ width: `${Math.min((day.count / Math.max(...weeklyProgress.map(d => d.count))) * 100, 100)}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
              </CardContent>
            </Card>

            {/* Progress Trends */}
            <Card className="bg-gradient-to-br from-background via-background to-muted/20 border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-foreground text-sm">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  Tendințe Progres
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 sm:space-y-4">
                <div className="flex items-center justify-between p-2 sm:p-4 bg-gray-800 rounded-lg">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
                    <span className="text-xs sm:text-sm text-foreground">Săptămâna aceasta</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="text-sm sm:text-lg font-bold text-foreground">{progressTrends.weeklyWorkouts}</span>
                    <Badge className="bg-green-600 text-white text-xs px-1.5 py-0.5">
                      <TrendingUp className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                      {progressTrends.weeklyTrend === 'up' ? 'Creștere' : 'Stabil'}
                    </Badge>
                  </div>
                </div>

                <div className="flex items-center justify-between p-2 sm:p-4 bg-gray-800 rounded-lg">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Timer className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500" />
                    <span className="text-xs sm:text-sm text-foreground">Luna aceasta</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="text-sm sm:text-lg font-bold text-foreground">{progressTrends.monthlyWorkouts}</span>
                    <Badge className="bg-orange-600 text-white text-xs px-1.5 py-0.5">
                      <TrendingUp className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                      {progressTrends.monthlyTrend === 'up' ? 'Creștere' : 'Stabil'}
                    </Badge>
                  </div>
                </div>

                <div className="flex items-center justify-between p-2 sm:p-4 bg-gray-800 rounded-lg">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Award className="h-5 w-5 text-purple-500" />
                    <span className="text-xs sm:text-sm text-foreground">Rata finalizare</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="text-sm sm:text-lg font-bold text-foreground">{overallStats.completionRate.toFixed(1)}%</span>
                    <Badge className="bg-purple-600 text-white">
                      <Target className="h-3 w-3 mr-1" />
                      {overallStats.completionRate > 70 ? 'Excelent' : overallStats.completionRate > 50 ? 'Bun' : 'Îmbunătățire'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="exercises" className="space-y-3">
          <Card className="bg-gradient-to-br from-background via-background to-muted/20 border-border">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-foreground text-sm">
                <Dumbbell className="h-4 w-4 text-blue-500" />
                Performanța Exercițiilor
              </CardTitle>
              <p className="text-xs text-muted-foreground">Analiza detaliată a performanței pentru fiecare exercițiu</p>
            </CardHeader>
            <CardContent>
              {/* Empty State */}
              <div className="text-center py-12">
                <Dumbbell className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2 text-foreground">Secțiunea este goală</h3>
                <p className="text-muted-foreground">
                  Această secțiune va fi populată cu date despre performanța exercițiilor în viitor.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="weekly" className="space-y-3">
          <Card className="bg-gradient-to-br from-background via-background to-muted/20 border-border">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-foreground text-sm">
                <Calendar className="h-4 w-4 text-orange-500" />
                Activitatea Săptămânală
              </CardTitle>
              <p className="text-xs text-muted-foreground">Progresul zilnic cu vizualizare dinamică</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Weekly Activity Chart - Compact */}
                <div className="relative">
                        {/* Chart Background */}
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5 rounded-lg"></div>
                        
                        {/* Days Grid - Mobile Responsive */}
                        <div className="relative grid grid-cols-7 gap-0.5 sm:gap-1 p-2 sm:p-4">
                    {weeklyProgress.map((day, index) => {
                      const maxCount = Math.max(...weeklyProgress.map(d => d.count));
                      const heightPercentage = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                      const isToday = new Date().toDateString() === new Date(day.date).toDateString();
                      
                      return (
                        <div key={day.date} className="flex flex-col items-center space-y-2">
                          {/* Activity Bar - Mobile Responsive */}
                          <div className="relative flex flex-col items-center justify-end h-12 sm:h-16 w-full">
                            {/* Bar Background */}
                            <div className="w-4 sm:w-6 bg-gray-700/30 rounded-t-md h-full flex items-end">
                              {/* Dynamic Bar */}
                              <div 
                                className={`w-full rounded-t-md transition-all duration-700 ease-out ${
                                  day.count > 0 
                                    ? 'bg-gradient-to-t from-blue-500 to-purple-500' 
                                    : 'bg-gray-600/20'
                                } ${isToday ? 'ring-1 ring-orange-400 ring-opacity-50' : ''}`}
                                style={{ 
                                  height: `${heightPercentage}%`,
                                  minHeight: day.count > 0 ? '4px' : '0px'
                                }}
                              >
                                {/* Activity Count Badge - Mobile Responsive */}
                                {day.count > 0 && (
                                  <div className="absolute -top-4 sm:-top-6 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs font-bold px-1 sm:px-1.5 py-0.5 rounded-full shadow-md">
                                    {day.count}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Day Info - Mobile Responsive */}
                            <div className="text-center">
                              <div className={`text-xs font-medium ${isToday ? 'text-orange-400' : 'text-muted-foreground'}`}>
                                {day.dayName}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(day.date).getDate()}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Summary Stats - Mobile Responsive */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-lg p-2 sm:p-3">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                        <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-blue-400 font-medium">Total Antrenamente</p>
                        <p className="text-sm sm:text-lg font-bold text-foreground">
                          {weeklyProgress.reduce((sum, day) => sum + day.count, 0)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-lg p-2 sm:p-3">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-lg flex items-center justify-center">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-green-400 font-medium">Zile Active</p>
                        <p className="text-sm sm:text-lg font-bold text-foreground">
                          {weeklyProgress.filter(day => day.count > 0).length}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-lg p-2 sm:p-3">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                        <Target className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-purple-400 font-medium">Media Zilnică</p>
                        <p className="text-sm sm:text-lg font-bold text-foreground">
                          {(weeklyProgress.reduce((sum, day) => sum + day.count, 0) / 7).toFixed(1)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activity Insights - Compact */}
                <div className="bg-gradient-to-br from-background via-background to-muted/10 border border-border rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-blue-500" />
                    Analiza Activitatii
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Cel mai activ zi</span>
                        <span className="text-xs font-medium text-foreground">
                          {weeklyProgress.reduce((max, day) => day.count > max.count ? day : max, weeklyProgress[0])?.dayName}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Antrenamente maxime</span>
                        <span className="text-xs font-medium text-foreground">
                          {Math.max(...weeklyProgress.map(d => d.count))}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Consistența</span>
                        <span className="text-xs font-medium text-foreground">
                          {((weeklyProgress.filter(day => day.count > 0).length / 7) * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Tendința</span>
                        <div className="flex items-center gap-1">
                          {weeklyProgress.filter(day => day.count > 0).length > 3 ? (
                            <>
                              <TrendingUp className="h-3 w-3 text-green-500" />
                              <span className="text-xs font-medium text-green-500">În creștere</span>
                            </>
                          ) : (
                            <>
                              <TrendingDown className="h-3 w-3 text-orange-500" />
                              <span className="text-xs font-medium text-orange-500">Necesită atenție</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-gradient-to-br from-background via-background to-muted/20 border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-foreground text-sm">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  Cele Mai Bune Performanțe
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {exercisePerformance.slice(0, 5).map((exercise, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                          {index + 1}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-foreground">{exercise.name}</div>
                          <div className="text-xs text-muted-foreground line-clamp-1">{exercise.category}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-orange-400">{exercise.bestWeight}kg</div>
                        <div className="text-xs text-gray-300">× {exercise.bestReps} reps</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-background via-background to-muted/20 border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-foreground text-sm">
                  <Award className="h-4 w-4 text-purple-500" />
                  Statistici Generale
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-gray-800 rounded-lg">
                    <div className="text-lg font-bold text-blue-400">{overallStats.uniqueExercises}</div>
                    <div className="text-xs text-muted-foreground">Exerciții Unice</div>
                  </div>
                  <div className="text-center p-3 bg-gray-800 rounded-lg">
                    <div className="text-lg font-bold text-green-400">{overallStats.activeDays}</div>
                    <div className="text-xs text-muted-foreground">Zile Active</div>
                  </div>
                  <div className="text-center p-3 bg-gray-800 rounded-lg">
                    <div className="text-lg font-bold text-orange-400">{overallStats.totalWorkouts}</div>
                    <div className="text-xs text-muted-foreground">Antrenamente Totale</div>
                  </div>
                  <div className="text-center p-3 bg-gray-800 rounded-lg">
                    <div className="text-lg font-bold text-purple-400">{overallStats.completionRate.toFixed(1)}%</div>
                    <div className="text-xs text-muted-foreground">Rata Finalizare</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
